import { useMemo } from 'react';
import { FieldName, FormLabel } from '@/constants';
import { usePageData } from '@/contexts/pageContext';
import { useShipping } from '@/contexts/shippingContext';
import { useGetCounties } from '@/hooks/useGetCounties';
import { getRequiredAddressFields } from '@/utils/getRequiredAddressFields';

export const useAddress = (
  isBillingAddress: boolean,
  excludedBillingCountryCodes?: string[],
) => {
  const { countries, additionalCountriesForBilling } = usePageData();
  const {
    orderPayload: { billingAddress, shippingAddress },
  } = useShipping();
  const { countryCode } = isBillingAddress ? billingAddress : shippingAddress;
  const counties = useGetCounties(countryCode);

  // List sent to the SearchableSelect component needs to have an id and should be generic.
  const mappedCountries = useMemo(() => {
    const mapped =
      countries?.map(country => ({
        id: country.countryCode,
        name: country.countryName,
      })) ?? [];

    // Add additional billing countries for billing address only
    if (isBillingAddress && additionalCountriesForBilling?.length) {
        const existingIds = new Set(mapped.map(c => c.id));
        const extras = additionalCountriesForBilling
            .filter(c => !existingIds.has(c.countryCode))
            .map(c => ({ id: c.countryCode, name: c.countryName }));
        mapped.push(...extras);

        // Re-sort: IRL first, GBR second, then alphabetical by name
        mapped.sort((a, b) => {
            if (a.id === 'IRL') return -1;
            if (b.id === 'IRL') return 1;
            if (a.id === 'GBR') return -1;
            if (b.id === 'GBR') return 1;
            return a.name.localeCompare(b.name);
        });
    }

    // Filter out excluded countries if exclusion list is provided via prop
    if (excludedBillingCountryCodes && excludedBillingCountryCodes.length > 0) {
      return mapped.filter(
        country => !excludedBillingCountryCodes.includes(country.id),
      );
    }

    return mapped;
  }, [countries, excludedBillingCountryCodes, additionalCountriesForBilling, isBillingAddress]);

  const mappedCounties = useMemo(
    () =>
      counties.map(county => ({
        id: county.countyCode,
        name: county.countyName,
      })),
    [counties],
  );

  const requiredFields = useMemo(
    () => getRequiredAddressFields(countryCode),
    [countryCode],
  );

  const isCountyRequired = useMemo(
    () => requiredFields.includes('countyCode'),
    [requiredFields],
  );

  const fieldData = useMemo(
    () => [
      {
        isRequired: true,
        label: FormLabel[FieldName.Country],
        name: FieldName.Country,
        options: mappedCountries,
        searchLabel: 'Search countries.',
      },
      {
        isRequired: true,
        label: FormLabel[FieldName.FirstName],
        name: FieldName.FirstName,
      },
      {
        isRequired: true,
        label: FormLabel[FieldName.LastName],
        name: FieldName.LastName,
      },
      {
        isRequired: false,
        label: `${FormLabel[FieldName.Company]} (optional)`,
        name: FieldName.Company,
      },
      {
        isRequired: true,
        label: FormLabel[FieldName.AddressLine1],
        name: FieldName.AddressLine1,
      },
      {
        isRequired: false,
        label: `${FormLabel[FieldName.AddressLine2]} (optional)`,
        name: FieldName.AddressLine2,
      },
      {
        isRequired: true,
        label: FormLabel[FieldName.City],
        name: FieldName.City,
      },
      {
        isRequired: isCountyRequired,
        label: FormLabel[FieldName.County],
        name: FieldName.County,
        options: mappedCounties,
        searchLabel: 'Search counties.',
      },
      {
        isRequired: true,
        label: FormLabel[FieldName.PostalCode],
        name: FieldName.PostalCode,
      },
      {
        isRequired: true,
        label: FormLabel[FieldName.Phone],
        name: FieldName.Phone,
      },
    ],
    [isCountyRequired, mappedCounties, mappedCountries],
  );

  return { fieldData };
};
